import { useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  MusicalNoteIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../../hooks/useAuth';
import { 
  MdLibraryMusic 
} from "react-icons/md";
import { FaHandSparkles } from "react-icons/fa6";
import { IoFootsteps } from "react-icons/io5";
import { GiRainbowStar } from 'react-icons/gi';
import { getUserSettings, saveUserSettings } from '../../services/firebase';
import { useGameProgress } from '../../hooks/useGameProgress';
import GameSettingsPanel from '../../components/games/GameSettingsPanel';
import TutorialOverlay from '../../components/games/TutorialOverlay';
import AudioManager from '../../components/games/AudioManager';
import { RAINBOW_DANCE_TUTORIAL } from '../../data/tutorials';
import toast from 'react-hot-toast';
import { GameProgress } from '../../types/game.d';
import { Category } from '../../types/category';

interface Player {
  id: string;
  name: string;
  score: number;
}

interface GameState {
  activeColors: { color: string; position: number; isHand?: boolean; isFoot?: boolean; isMusic?: boolean }[];
  isPlaying: boolean;
  level: number;
  mode: 'hands' | 'feet' | 'combo';
  interval: number;
  score: number;
  isAudioEnabled: boolean;
  isFullscreen: boolean;
  players: Player[];
  roundStartTime: number;
  currentRound: number;
}

interface ActivityType {
  id: string;    // Unique identifier for the activity type
  name: string;  // Name of the activity type
}

interface GameSettings {
  musicVolume: number;
  interval: number;
  mode: 'hands' | 'feet' | 'combo';
  difficulty: 'easy' | 'medium' | 'hard';
  musicTrack: string;
  isFullscreen: boolean;
  numberOfPlayers: number;
  categories: Category[];
  activityTypes: ActivityType[];
  theme: string;
  patternLength: number;
}

interface DifficultySettings {
  baseInterval: number;
  intervalDecrease: number;
  minInterval: number;
  maxPatternLength: number;
  comboUnlockLevel: number;
}

const COLORS = [
  { name: 'green', value: '#22c55e' },
  { name: 'red', value: '#ef4444' },
  { name: 'yellow', value: '#eab308' },
  { name: 'blue', value: '#3b82f6' }
];

const POSITIONS = [
  { x: 0, y: 0 }, // top-left
  { x: 1, y: 0 }, // top-right
  { x: 0, y: 1 }, // bottom-left
  { x: 1, y: 1 }  // bottom-right
];

const DEFAULT_SETTINGS: GameSettings = {
  musicVolume: 0.5,
  interval: 5000,
  isFullscreen: false,
  mode: 'feet',
  difficulty: 'medium',
  musicTrack: 'track1',
  numberOfPlayers: 2,
  categories: [{ id: 'default', name: 'Default Category', type: 'standard' }],  // Initialized with a placeholder
  activityTypes: [{ id: 'default', name: 'Default Activity' }],  // Initialized with a placeholder
  theme: 'defaultTheme',
  patternLength: 1,
};

const DIFFICULTY_CONFIG: Record<string, DifficultySettings> = {
  easy: {
    baseInterval: 5000,
    intervalDecrease: 200,
    minInterval: 2000,
    maxPatternLength: 2,
    comboUnlockLevel: 3
  },
  medium: {
    baseInterval: 4000,
    intervalDecrease: 300,
    minInterval: 1500,
    maxPatternLength: 3,
    comboUnlockLevel: 2
  },
  hard: {
    baseInterval: 3000,
    intervalDecrease: 400,
    minInterval: 1000,
    maxPatternLength: 4,
    comboUnlockLevel: 1
  }
};

const getPatternWeights = (difficulty: string, level: number) => {
  const weights = {
    single: 100,
    music_only: 0,
    mixed: 0,
    hands_feet: 0
  };

  if (level >= DIFFICULTY_CONFIG[difficulty].comboUnlockLevel) {
    weights.music_only = 20 + Math.min(level * 5, 40);
    weights.mixed = 10 + Math.min(level * 3, 30);
    weights.hands_feet = Math.max(0, Math.min(level * 2 - 10, 20));
    weights.single = Math.max(20, 100 - weights.music_only - weights.mixed - weights.hands_feet);
  }

  return weights;
};

const weightedRandom = (weights: Record<string, number>) => {
  const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const [item, weight] of Object.entries(weights)) {
    if (random < weight) return item;
    random -= weight;
  }
  return Object.keys(weights)[0];
};

export default function RainbowDance() {
  const navigate = useNavigate();
  const { user, isGuest, logout, continueAsGuest, isLoading, error } = useAuth();
  const userId = user?.uid || '';
  const { settings, stats, updateProgress } = useGameProgress('rainbow-dance', userId);
  
  // Create a local storage management system
  const [gameSettings, setGameSettings] = useState<GameSettings | null>(() => {
    // Ensure all settings are pulled from persistent storage or use defaults
    if (userId) {
      return settings;
    } else {
      const settings = JSON.parse(localStorage.getItem('gameSettings') || '{}');
      return settings;
    }
  });

  const [isPlayerSetupOpen, setIsPlayerSetupOpen] = useState(false);
  const [playerNames, setPlayerNames] = useState<string[]>(
    Array(DEFAULT_SETTINGS.numberOfPlayers).fill('')
  );
  const [showSettings, setShowSettings] = useState(false);
  const [showTutorial, setShowTutorial] = useState(!localStorage.getItem('tutorial_completed_rainbow-dance'));
  const [gameState, setGameState] = useState<GameState>({
    activeColors: [],
    isPlaying: false,
    level: 1,
    mode: 'feet',
    interval: DIFFICULTY_CONFIG[gameSettings?.difficulty || 'medium'].baseInterval,
    score: 0,
    isAudioEnabled: false,
    isFullscreen: false,
    players: [],
    roundStartTime: 0,
    currentRound: 1
  });

  const ROUND_DURATION = 60000; // 1 minute in milliseconds

  const audioTracks = AudioManager.getTracks();
  const [selectedTrack, setSelectedTrack] = useState(audioTracks[0]);
  const [volume, setVolume] = useState(gameSettings?.musicVolume || 0.5);
  const [showMusicControls, setShowMusicControls] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const handleTrackChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const track = event.target.value;
    setSelectedTrack(track);
    AudioManager.setTrack(track);
    AudioManager.play();
  };

  const handlePlayPause = () => {
    const isPlaying = AudioManager.music.playing();
    if (isPlaying) {
      AudioManager.pause();
    } else {
      AudioManager.play();
    }
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    AudioManager.music.volume(newVolume);
    if (gameSettings) {
      setGameSettings({ ...gameSettings, musicVolume: newVolume });
    }
  };

  const toggleMusicControls = () => {
    setShowMusicControls(!showMusicControls);
  };

  useEffect(() => {
    let gameTimer: NodeJS.Timeout;
    let roundTimer: NodeJS.Timeout;

    if (gameState.isPlaying) {
      // Start music
      AudioManager.play();

      // Set round start time
      setGameState(prev => ({
        ...prev,
        roundStartTime: Date.now()
      }));

      // Round timer - check every second for round completion
      roundTimer = setInterval(() => {
        const elapsedTime = Date.now() - gameState.roundStartTime;
        const remainingTime = ROUND_DURATION - elapsedTime;

        // Adjust playback speed as the round progresses
        const speed = 1 + (1 - remainingTime / ROUND_DURATION);
        AudioManager.setSpeed(speed);

        if (elapsedTime >= ROUND_DURATION) {
          // Round complete
          setGameState(prev => ({
            ...prev,
            currentRound: prev.currentRound + 1,
            roundStartTime: Date.now(),
            level: prev.level + 1
          }));
          toast.success(`Round ${gameState.currentRound} complete!`);
        }
      }, 1000);

      // Pattern generation timer
      const difficulty = gameSettings?.difficulty || 'medium';
      const config = DIFFICULTY_CONFIG[difficulty as keyof typeof DIFFICULTY_CONFIG];
      const currentInterval = Math.max(
        config?.minInterval ?? 1000,
        (config?.baseInterval ?? 5000) - (gameState.level - 1) * (config?.intervalDecrease ?? 0)
      );

      gameTimer = setInterval(() => {
        setGameState(prev => ({
          ...prev,
          activeColors: generatePattern(gameSettings?.difficulty || 'medium', prev.level),
          interval: Math.max(
            config?.minInterval ?? 1000,
            (config?.baseInterval ?? 5000) - (prev.level - 1) * (config?.intervalDecrease ?? 0)
          )
        }));
      }, currentInterval);
    } else {
      // Stop music
      AudioManager.stop();
    }

    // Cleanup function
    return () => {
      if (gameTimer) clearInterval(gameTimer);
      if (roundTimer) clearInterval(roundTimer);
      AudioManager.stop();
    };
  }, [gameState.isPlaying, gameState.level, gameSettings?.difficulty]);

  const generatePattern = useCallback((difficulty: string, level: number) => {
    const positions = [...Array(4)].map((_, i) => i);
    const pattern = [];
    const config = DIFFICULTY_CONFIG[difficulty as keyof typeof DIFFICULTY_CONFIG];
    
    if (gameSettings?.mode === 'combo') {
      const weights = getPatternWeights(difficulty, level);
      const selectedPattern = weightedRandom(weights);
      
      switch (selectedPattern) {
        case 'music_only': {
          const musicCount = Math.min(
            Math.floor(level / (config?.comboUnlockLevel ?? 3)),
            2
          );
          for (let i = 0; i < musicCount; i++) {
            const colorIndex = Math.floor(Math.random() * COLORS.length);
            const positionIndex = Math.floor(Math.random() * positions.length);
            const position = positions.splice(positionIndex, 1)[0];
            pattern.push({
              color: COLORS[colorIndex].value,
              position,
              isMusic: true
            });
          }
          break;
        }
        case 'mixed': {
          const types = ['music', 'hand', 'foot'];
          for (const type of types) {
            const colorIndex = Math.floor(Math.random() * COLORS.length);
            const positionIndex = Math.floor(Math.random() * positions.length);
            const position = positions.splice(positionIndex, 1)[0];
            pattern.push({
              color: COLORS[colorIndex].value,
              position,
              isHand: type === 'hand',
              isFoot: type === 'foot',
              isMusic: type === 'music'
            });
          }
          break;
        }
        case 'hands_feet': {
          const handCount = Math.min(Math.floor(level / 2), 2);
          const footCount = Math.min(Math.floor(level / 2), 2);
          
          for (let i = 0; i < handCount; i++) {
            const colorIndex = Math.floor(Math.random() * COLORS.length);
            const positionIndex = Math.floor(Math.random() * positions.length);
            const position = positions.splice(positionIndex, 1)[0];
            pattern.push({
              color: COLORS[colorIndex].value,
              position,
              isHand: true
            });
          }
          
          for (let i = 0; i < footCount; i++) {
            const colorIndex = Math.floor(Math.random() * COLORS.length);
            const positionIndex = Math.floor(Math.random() * positions.length);
            const position = positions.splice(positionIndex, 1)[0];
            pattern.push({
              color: COLORS[colorIndex].value,
              position,
              isFoot: true
            });
          }
          break;
        }
        case 'single': {
          const type = ['hand', 'foot', 'music'][Math.floor(Math.random() * 3)];
          const colorIndex = Math.floor(Math.random() * COLORS.length);
          const positionIndex = Math.floor(Math.random() * positions.length);
          const position = positions.splice(positionIndex, 1)[0];
          pattern.push({
            color: COLORS[colorIndex].value,
            position,
            isHand: type === 'hand',
            isFoot: type === 'foot',
            isMusic: type === 'music'
          });
          break;
        }
      }
    } else {
      // Non-combo modes
      const patternLength = Math.min(
        Math.ceil(level / 2),
        config?.maxPatternLength ?? 3
      );
      
      for (let i = 0; i < patternLength; i++) {
        const colorIndex = Math.floor(Math.random() * COLORS.length);
        const positionIndex = Math.floor(Math.random() * positions.length);
        const position = positions.splice(positionIndex, 1)[0];
        
        pattern.push({
          color: COLORS[colorIndex].value,
          position,
          isHand: gameSettings?.mode === 'hands',
          isFoot: gameSettings?.mode === 'feet'
        });
      }
    }

    // Add logging to verify pattern generation
    console.log('Pattern Elements:', pattern.map((element) => ({ color: element.color, position: element.position, isHand: element.isHand, isFoot: element.isFoot, isMusic: element.isMusic })));

    return pattern;
  }, [gameSettings?.mode]);

  const renderGameSquareLogo = (activeColor: any) => {
    if (gameSettings?.mode === 'combo') {
      if (activeColor.isMusic) {
        return <MdLibraryMusic className="h-12 w-12 text-blue-500" />;
      } else if (activeColor.isHand) {
        return <FaHandSparkles className="h-12 w-12 text-purple-500" />;
      } else if (activeColor.isFoot) {
        return <IoFootsteps className="h-12 w-12 text-green-500" />;
      }
    } else {
      switch (gameSettings?.mode) {
        case 'hands':
          return <FaHandSparkles className="h-12 w-12 text-purple-500" />;
        case 'feet':
          return <IoFootsteps className="h-12 w-12 text-green-500" />;
        default:
          return <GiRainbowStar size={50} color="rainbow" />;
      }
    }
  };

  const handlePlayerSetup = () => {
    if (playerNames && playerNames.filter(name => name.trim()).length < 2) {
      toast.error('Please enter at least 2 player names');
      return;
    }
    const validPlayers = playerNames ? playerNames.filter(name => name.trim() !== '') : [];
    const players: Player[] = validPlayers?.map((name, index) => ({
      id: `player_${index + 1}`,
      name,
      score: 0
    }));

    setGameState(prev => ({
      ...prev,
      players,
      isPlaying: true,
      activeColors: generatePattern(gameSettings?.difficulty || 'medium', 1),
      level: 1,
      score: 0
    }));
    setIsPlayerSetupOpen(false);
  };

  const enterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setGameState(prev => ({ 
        ...prev, 
        isFullscreen: prev.isFullscreen !== undefined ? prev.isFullscreen : false 
      }));
    } catch (error) {
      console.error('Fullscreen request failed:', error);
      toast.error('Could not enter fullscreen mode');
    }
  };

  const exitFullscreen = async () => {
    try {
      await document.exitFullscreen();
      setGameState(prev => ({ 
        ...prev, 
        isFullscreen: prev.isFullscreen !== undefined ? prev.isFullscreen : false 
      }));
    } catch (error) {
      console.error('Exit fullscreen failed:', error);
      toast.error('Could not exit fullscreen mode');
    }
  };

  const handleBack = () => {
    navigate('/diy-activities');
  };

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  }, []);

  const stopGame = () => {
    updateProgress({
      stats: {
        highScore: Math.max(stats.highScore, gameState.score),
        totalGamesPlayed: stats.totalGamesPlayed + 1,
        averageScore: Math.round((stats.averageScore * stats.totalGamesPlayed + gameState.score) / (stats.totalGamesPlayed + 1)),
        longestStreak: Math.max(stats.longestStreak, gameState.level),
        lastPlayed: new Date()
      }
    });

    setGameState(prev => ({
      ...prev,
      isPlaying: false,
      activeColors: [],
      level: 1,
      score: 0
    }));

    if (document.fullscreenElement) {
      exitFullscreen();
    }
  };

  const startGame = () => {
    if (!gameState.players.length) {
      toast.error('Please set up players first', {
        duration: 4000,
        position: 'top-center',
        style: {
          minWidth: '250px',
          zIndex: 9999
        },
        className: 'error-toast',
        ariaProps: {
          role: 'status',
          'aria-live': 'polite'
        }
      });
      return;
    }

    if (gameSettings?.isFullscreen) {
      enterFullscreen();
    }

    setGameState(prev => ({
      ...prev,
      isPlaying: true,
      activeColors: generatePattern(gameSettings?.difficulty || 'medium', prev.level),
      level: 1,
      score: 0
    }));
  };

  const handleSaveProgress = async (newProgress: Partial<GameProgress>) => {
    updateProgress({
      stats: {
        highScore: newProgress.stats?.highScore || stats.highScore,
        totalGamesPlayed: newProgress.stats?.totalGamesPlayed || stats.totalGamesPlayed,
        averageScore: newProgress.stats?.averageScore || stats.averageScore,
        longestStreak: newProgress.stats?.longestStreak || stats.longestStreak,
        lastPlayed: newProgress.stats?.lastPlayed || new Date()
      }
    });
  };

  const handleSaveSettings = async (newSettings: GameSettings) => {
    setGameSettings(newSettings);
    await saveUserSettings(userId, newSettings);
  };

  const handleSaveUserSettings = async (settings: GameSettings) => {
    await getUserSettings(userId);
    updateProgress({ settings });
  };

  useEffect(() => {
    if (isGuest) {
      console.log('You are playing as a guest. Log in for more features!');
    }
  }, [isGuest]);

  const handleGameStart = () => {
    if (isGuest) {
      console.log('Starting game as a guest');
    } else {
      console.log('Starting game as a logged-in user');
    }
  };

  useEffect(() => {
    handleGameStart();
  }, []);

  useEffect(() => {
    updateProgress({
      stats: {
        lastPlayed: new Date(),
        highScore: Math.max(gameState.score, stats?.highScore || 0),
        totalGamesPlayed: (stats?.totalGamesPlayed || 0) + 1,
        averageScore: stats?.averageScore || gameState.score,
        longestStreak: stats?.longestStreak || 0
      }
    });
  }, [userId, gameState.score, stats]);

  const [scoreInputs, setScoreInputs] = useState<Record<string, string>>({});

  const handleScoreChange = (playerId: string, value: string) => {
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setScoreInputs(prev => ({
        ...prev,
        [playerId]: value
      }));
    }
  };

  const handleScoreSubmit = (playerId: string) => {
    const score = parseInt(scoreInputs[playerId] || '0', 10);
    setGameState(prev => ({
      ...prev,
      players: prev.players.map(player => 
        player.id === playerId
          ? { ...player, score: player.score + score }
          : player
      )
    }));
    // Clear the input after submission
    setScoreInputs(prev => ({
      ...prev,
      [playerId]: ''
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleGuestMode = () => {
    continueAsGuest();
    navigate('/');
  };

  const [showSymbolInfo, setShowSymbolInfo] = useState(false);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        {typeof error === 'string' 
          ? error 
          : (error as { message?: string })?.message || 'An unknown error occurred'}
      </div>
    );
  }

  const updatedSettings = {
    ...gameSettings, 
    musicVolume: gameSettings?.musicVolume !== undefined ? gameSettings.musicVolume : 0,    categories: [], 
    activityTypes: [], 
    theme: 'defaultTheme',
    patternLength: 1,
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {error && (
        <div className="error-message">
          {typeof error === 'string' 
            ? error 
            : (error as { message?: string })?.message || 'An unknown error occurred'}
        </div>
      )}
      
      {isPlayerSetupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Setup Players</h2>
            {playerNames?.map((name, index) => (
              <div key={index} className="mb-2">
                <label className="block mb-1">Player {index + 1} Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    const newNames = [...playerNames];
                    newNames[index] = e.target.value;
                    setPlayerNames(newNames);
                  }}
                  className="w-full px-2 py-1 border rounded"
                  placeholder={`Enter Player ${index + 1} name`}
                />
              </div>
            ))}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setIsPlayerSetupOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handlePlayerSetup}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Start Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Stats Display in Navigation Bar */}
      {gameState.isPlaying && (
        <div className="flex items-center space-x-4 text-white">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Level:</span>
            <span className="text-lg font-bold text-green-400">{gameState.level}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Round:</span>
            <span className="text-lg font-bold text-purple-400">{gameState.currentRound}</span>
          </div>
        </div>
      )}

      {/* Player Scores Sidebar */}
      {gameState.players.length > 0 && (
        <div className="fixed top-1/2 right-4 transform -translate-y-1/2 
          w-64 bg-gray-800/70 rounded-lg p-4 
          hidden md:flex flex-col space-y-4 
          z-20">
          {gameState.players.map((player) => (
            <div 
              key={player.id} 
              className="p-3 rounded-md bg-gray-700/70"
            >
              <div className="font-semibold text-white">{player.name}</div>
              <div className="text-gray-300 mb-2">Score: {player.score}</div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={scoreInputs[player.id] || ''}
                  onChange={(e) => handleScoreChange(player.id, e.target.value)}
                  className="w-16 px-2 py-1 text-black rounded"
                  placeholder="0"
                />
                <button
                  onClick={() => handleScoreSubmit(player.id)}
                  className="px-2 py-1 bg-blue-500 rounded hover:bg-blue-600"
                >
                  Add
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-gray-800 p-4 flex items-center justify-between z-10">
        <button
          onClick={handleBack}
          className="group flex items-center justify-center p-2 rounded-full transition-all duration-300 
            bg-gray-700 hover:bg-blue-600 
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            active:scale-95 shadow-md hover:shadow-lg"
          title="Return to DIY Activities"
        >
          <ArrowLeftIcon className="h-6 w-6 text-white group-hover:text-white/90 transition-colors" />
        </button>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowSymbolInfo(true)}
            className="p-2 rounded-md hover:bg-gray-700"
            title="Symbol Information"
          >
            <InformationCircleIcon className="h-6 w-6" />
          </button>
          <button
            onClick={toggleFullScreen}
            title={gameState.isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            className={`p-2 rounded-md ${
              gameState.isFullscreen ? 'bg-blue-600' : 'bg-gray-700 hover:bg-blue-600'
            }`}
          >
            {gameState.isFullscreen 
              ? <ArrowsPointingInIcon className="h-6 w-6" /> 
              : <ArrowsPointingOutIcon className="h-6 w-6" />}
          </button>

          <button
            onClick={() => setShowSettings(true)}
            className="p-2 rounded-md hover:bg-gray-700"
            title="Game Settings"
          >
            <Cog6ToothIcon className="h-6 w-6" />
          </button>
          <button
            onClick={gameState.isPlaying ? stopGame : startGame}
            title={gameState.isPlaying ? 'Stop Game' : 'Start Game'}
            aria-label={gameState.isPlaying ? 'Stop Game' : 'Start Game'}
            className={`px-4 py-2 rounded-md ${
              gameState.isPlaying ? 'bg-red-600' : 'bg-green-600'
            }`}
          >
            {gameState.isPlaying ? 'Stop' : 'Start'}
          </button>
          <button
            onClick={() => setIsPlayerSetupOpen(true)}
            title="Player Setup"
            aria-label="Player Setup"
            className="px-4 py-2 rounded-md bg-purple-600"
          >
            Players
          </button>
          <button
            onClick={async () => {
              const settingsToSave = {
                ...DEFAULT_SETTINGS,
                ...updatedSettings,
                interval: updatedSettings.interval ?? DEFAULT_SETTINGS.interval,
                mode: updatedSettings.mode ?? DEFAULT_SETTINGS.mode,
                difficulty: updatedSettings.difficulty ?? DEFAULT_SETTINGS.difficulty,
                musicTrack: updatedSettings.musicTrack ?? DEFAULT_SETTINGS.musicTrack,
                theme: updatedSettings.theme ?? DEFAULT_SETTINGS.theme,
                patternLength: updatedSettings.patternLength ?? DEFAULT_SETTINGS.patternLength
              };
              try {
                await handleSaveUserSettings(settingsToSave);
                await handleSaveProgress({ stats });
                toast.success('Game progress saved successfully!');
              } catch (error) {
                toast.error('Failed to save game progress');
              }
            }}
            disabled={!userId}
            className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Game
          </button>
          <button
            onClick={toggleMusicControls}
            className={`p-2 rounded-md ${
              gameState.isAudioEnabled ? 'bg-blue-600' : 'bg-blue-400'
            } hover:bg-blue-500 transition-colors duration-200`}
            title="Music Controls"
          >
            <MusicalNoteIcon className="h-6 w-6 text-white" />
          </button>
          {showMusicControls && (
            <div className="music-controls absolute top-14 right-4 bg-gray-900 p-4 rounded-lg shadow-lg">
              <label className="block text-white mb-2">Select Track:</label>
              <select
                value={selectedTrack}
                onChange={handleTrackChange}
                className="mb-2 p-1 rounded-md bg-gray-700 text-white w-full"
              >
                {audioTracks.map(track => (
                  <option key={track} value={track}>
                    {AudioManager.getTrackAlias(track)}
                  </option>
                ))}
              </select>
              <button
                onClick={handlePlayPause}
                className="mb-2 w-full p-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white transition-colors duration-200"
              >
                {AudioManager.music.playing() ? 'Pause' : 'Play'}
              </button>
              <label className="block text-white mb-2">Volume:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-full"
              />
            </div>
          )}
          {/* User Authentication Button */}
          <Menu as="div" className="relative ml-3">
            <Menu.Button className="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              {user?.photoURL ? (
                <img
                  className="h-8 w-8 rounded-full"
                  src={user.photoURL}
                  alt={user.displayName || 'User profile'}
                />
              ) : (
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
              )}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {user ? (
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings/profile"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          }`}
                        >
                          Profile Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          }`}
                        >
                          Sign Out
                        </button>
                      )}
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/login')}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          }`}
                        >
                          Sign In
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleGuestMode}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          }`}
                        >
                          Continue as Guest
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Game Grid */}
      <div className="pt-20 pb-4 px-4 flex items-center justify-center min-h-screen">
        <div className="grid grid-cols-2 gap-4 w-full max-w-2xl aspect-square">
          {POSITIONS.map((_, index) => {
            const activeColor = gameState.activeColors.find(
              c => c.position === index
            );
            
            return (
              <div
                key={index}
                className={`relative rounded-lg transition-colors duration-300 ${
                  activeColor ? 'opacity-100' : 'opacity-30'
                }`}
                style={{
                  backgroundColor: activeColor?.color || COLORS[index].value
                }}
              >
                {/* Always show the appropriate icon based on game mode when there's an active color */}
                {activeColor && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {renderGameSquareLogo(activeColor)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Symbol Info Modal */}
      {showSymbolInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-white">Symbol Types</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <FaHandSparkles className="h-8 w-8 text-purple-500" />
                <span className="text-white">Hand Symbol - Place a hand on this color!</span>
              </div>
              <div className="flex items-center space-x-3">
                <IoFootsteps className="h-8 w-8 text-green-500" />
                <span className="text-white">Foot Symbol - Place a foot on this color!</span>
              </div>
              <div className="flex items-center space-x-3">
                <MdLibraryMusic className="h-8 w-8 text-blue-500" />
                <span className="text-white">Music Symbol - Place a hand and foot on this color!</span>
              </div>
            </div>
            <button
              onClick={() => setShowSymbolInfo(false)}
              className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Settings Panel */}
      <GameSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={gameSettings}
        onUpdateSettings={handleSaveSettings}
        onResetSettings={() => handleSaveSettings({
          musicVolume: 50,
          interval: 1000,
          mode: 'hands',
          difficulty: 'medium',
          musicTrack: '',
          isFullscreen: false,
          numberOfPlayers: 1,
          categories: [],
          activityTypes: [],
          theme: 'defaultTheme',
          patternLength: 1,
        })}
      />
      
      {/* Tutorial Overlay */}
      <TutorialOverlay
        steps={RAINBOW_DANCE_TUTORIAL}
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={() => {
          localStorage.setItem('tutorial_completed_rainbow-dance', 'true');
          setShowTutorial(false);
        }}
      />
      
      {/* Stats Display */}
      {!gameState.isPlaying && gameState.players.length > 0 && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800/90 p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-4">Game Over!</h2>
          <div className="space-y-4">
            {gameState.players.map((player) => (
              <div 
                key={player.id} 
                className="p-3 rounded-md bg-gray-700/70"
              >
                <div className="font-semibold text-white">{player.name}</div>
                <div className="text-gray-300">Final Score: {player.score}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <audio ref={audioRef} />
    </div>
  );
}